import React from 'react';

interface CompanyProfileTabProps {
  formData: {
    companyName: string;
    address: string;
    contactNumber: string;
    termsOfService: string;
    about: string;
    emailAddress: string;
    taxAmount: number;
    logoUrl: string;
  };
  logoPreview: string | null;
  logoFile: File | null;
  saving: boolean;
  onFieldChange: (field: string, value: string | number) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: (event: React.FormEvent) => void;
}

export const CompanyProfileTab: React.FC<CompanyProfileTabProps> = ({
  formData,
  logoPreview,
  saving,
  onFieldChange,
  onFileChange,
  onSave,
}) => {
  return (
    <form onSubmit={onSave} className="space-y-5 text-left">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-700">Company Name *</label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => onFieldChange('companyName', e.target.value)}
            className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
            placeholder="e.g. Strivre Services"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-700">Contact Number *</label>
          <input
            type="text"
            value={formData.contactNumber}
            onChange={(e) => onFieldChange('contactNumber', e.target.value)}
            className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
            placeholder="+1 (555) 000-0000"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Address</label>
        <textarea
          value={formData.address}
          onChange={(e) => onFieldChange('address', e.target.value)}
          rows={3}
          className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all resize-none"
          placeholder="Enter company address"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Email Address</label>
        <input
          type="email"
          value={formData.emailAddress}
          onChange={(e) => onFieldChange('emailAddress', e.target.value)}
          className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
          placeholder="Enter company email address"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Terms of Service URL</label>
        <input
          type="text"
          value={formData.termsOfService}
          onChange={(e) => onFieldChange('termsOfService', e.target.value)}
          className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
          placeholder="Enter your company terms of service url..."
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">About Your Company</label>
        <textarea
          value={formData.about || ''}
          onChange={(e) => onFieldChange('about', e.target.value)}
          className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all resize-y min-h-[120px]"
          placeholder="Write a short description about your business..."
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Company Logo</label>
        <div className="flex flex-col gap-4">
          <div className="min-w-[8rem] min-h-[8rem] w-fit max-w-[18rem] bg-slate-50 border border-strivre-lightnavy rounded-xl flex items-center justify-center p-4 overflow-hidden shrink-0 shadow-sm transition-all duration-300">
            {logoPreview || formData.logoUrl ? (
              <img
                src={logoPreview || formData.logoUrl}
                alt="Logo preview"
                className="max-h-24 w-auto object-contain border border-slate-100 rounded-lg p-1 bg-white shadow-sm"
              />
            ) : (
              <div className="w-20 h-20 bg-strivre-burgundy rounded-xl flex items-center justify-center font-bold text-white text-3xl uppercase shadow-md">
                {formData.companyName?.charAt(0) || 'S'}
              </div>
            )}
          </div>

          <div className="flex-1">
            <input
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="w-full max-w-sm bg-white border border-strivre-lightnavy rounded-xl px-4 py-2 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-strivre-lightburgundy file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* <div className="pt-2 border-t border-slate-100 flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-strivre-burgundy hover:bg-red-900 disabled:bg-red-800 text-white px-8 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-red-200 active:scale-95"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={18} />}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div> */}
    </form>
  );
};
